import { EscalationRiskTier } from "@remi/enums/escalation-risk-tier";
import { EscalationStage } from "@remi/enums/escalation-stage";
import { TaskReadiness } from "@remi/enums/task/readiness";
import { TaskStatus } from "@remi/enums/task-status";
import { TicketPriority } from "@remi/enums/ticket/priority";
import { RemiErrorDeprecated } from "@remi/error";
import { entries, isOneOf } from "@remi/ts-utils";
import { INBOX_SCOPE, Inbox, InboxValues, TASK_DUE_DATE_BOUNDARY_TOKEN, type TeamInbox } from "@remi/types/inbox";
import {
  Comparator,
  type Condition,
  type Definition,
  MySqlValue,
  type SortDefinition,
} from "@remi/types/query-builder";
import { InboxItemType } from "@remi/types/table";
import { ACTIVE_ESCALATION_STAGES } from "@remi/types-enums/escalation";
import {
  ACTIVE_TICKET_STATUSES,
  OPEN_TICKET_STATUSES,
  TRIAGE_TICKET_STATUSES,
} from "@remi/types-enums/ticket/status-group";

export type ConditionState = {
  status: Condition | undefined;
  priority: Condition | undefined;
  assigneeId: Condition | undefined;
  teamId: Condition | undefined;
  subcategory: Condition | undefined;
  type: Condition | undefined;
  conversationType: Condition | undefined;
  conversationChannel: Condition | undefined;
  partnerFlow: Condition | undefined;
  snoozed: Condition | undefined;
  dueDate: Condition | undefined;
  isResolved: Condition | undefined;
  needsCheckIn: Condition | undefined;
  createdById: Condition | undefined;
  taskStatus: Condition | undefined;
  readiness: Condition | undefined;
  needsReview: Condition | undefined;
  taskType: Condition | undefined;
  projectCategory: Condition | undefined;
  homeownerSentiment: Condition | undefined;
  tagId: Condition | undefined;
  riskTier: Condition | undefined;
  resolutionStage: Condition | undefined;
  hasOpenConversation: Condition | undefined;
  contractorId: Condition | undefined;
  waitingOn: Condition | undefined;
  latestItemKind: Condition | undefined;
  latestItemStormTag: Condition | undefined;
  createdAt: Condition | undefined;
  updatedAt: Condition | undefined;
  followUpDate: Condition | undefined;
  lastUnresolvedAt: Condition | undefined;
  snoozeDate: Condition | undefined;
};

export const emptyConditionState: ConditionState = {
  type: undefined,
  assigneeId: undefined,
  priority: undefined,
  status: undefined,
  subcategory: undefined,
  teamId: undefined,
  snoozed: undefined,
  dueDate: undefined,
  isResolved: undefined,
  needsCheckIn: undefined,
  homeownerSentiment: undefined,
  tagId: undefined,
  conversationType: undefined,
  conversationChannel: undefined,
  partnerFlow: undefined,
  createdById: undefined,
  taskStatus: undefined,
  readiness: undefined,
  needsReview: undefined,
  taskType: undefined,
  projectCategory: undefined,
  riskTier: undefined,
  resolutionStage: undefined,
  hasOpenConversation: undefined,
  contractorId: undefined,
  waitingOn: undefined,
  latestItemKind: undefined,
  latestItemStormTag: undefined,
  createdAt: undefined,
  updatedAt: undefined,
  followUpDate: undefined,
  lastUnresolvedAt: undefined,
  snoozeDate: undefined,
};

export const encodeConditions = (conditions: ConditionState): Partial<ConditionState> => {
  const active: Partial<ConditionState> = {};
  for (const [field, condition] of entries(conditions)) {
    if (condition) {
      active[field] = condition;
    }
  }
  return active;
};

type ConditionField = keyof ConditionState;
type ConditionFieldArray = Array<ConditionField>;

const ticketFields = [
  "status",
  "priority",
  "assigneeId",
  "teamId",
  "subcategory",
  "snoozed",
  "createdById",
  "homeownerSentiment",
  "tagId",
  "partnerFlow",
  "contractorId",
  "createdAt",
  "updatedAt",
  "snoozeDate",
] as const satisfies ConditionFieldArray;

const conversationFields = [
  "assigneeId",
  "teamId",
  "isResolved",
  "needsCheckIn",
  "conversationType",
  "conversationChannel",
  "partnerFlow",
  "homeownerSentiment",
  "contractorId",
  "latestItemKind",
  "latestItemStormTag",
  "createdAt",
  "updatedAt",
  "lastUnresolvedAt",
] as const satisfies ConditionFieldArray;

export const taskFields = [
  "assigneeId",
  "teamId",
  "taskStatus",
  "readiness",
  "needsReview",
  "taskType",
  "projectCategory",
  "partnerFlow",
  "homeownerSentiment",
  "dueDate",
  "contractorId",
  "waitingOn",
  "createdAt",
  "updatedAt",
  "followUpDate",
] as const satisfies ConditionFieldArray;

const escalationFields = [
  "priority",
  "assigneeId",
  "teamId",
  "riskTier",
  "resolutionStage",
  "hasOpenConversation",
  "snoozed",
  "contractorId",
  "createdAt",
  "updatedAt",
  "followUpDate",
] as const satisfies ConditionFieldArray;

// getSnoozeDefinition rewrites `snoozed` to `followUpDate` before the SQL layer.
// getSnoozeDefinition and the snoozeDate range slot both rewrite to `followUpDate` before the SQL layer.
export type TicketSqlField = Exclude<(typeof ticketFields)[number], "snoozed" | "snoozeDate"> | "followUpDate";
// followUpDate (COALESCE(latestOrderDate, createdAt)) stays API-filterable but has no UI slot —
// the conversation UI filters lastUnresolvedAt instead.
export type ConversationSqlField = (typeof conversationFields)[number] | "followUpDate";
// getDueDateDefinition rewrites `dueDate` to `followUpDate`; getNeedsReviewDefinition rewrites
// `needsReview` to `taskStatus` (or drops it entirely) before the SQL layer.
export type TaskSqlField = Exclude<(typeof taskFields)[number], "dueDate" | "needsReview">;
export type EscalationSqlField = Exclude<(typeof escalationFields)[number], "snoozed"> | "snoozedUntil";

const ACTIVE_TASK_READINESS = [TaskReadiness.READY] as const;
const TERMINAL_ESCALATION_STAGES = [EscalationStage.COMPLETION, EscalationStage.DISMISSED] as const;
const TRIAGE_ESCALATION_STAGES = [EscalationStage.NOT_CONTACTED] as const;
// EscalationTableColumns (domain-services inbox/query.ts) resolves riskTier to
// COALESCE(WorkSite.riskTierOverride, WorkSite.riskTier) — the effective tier, not the stale
// Escalation.riskTier column.
const BANNER_RISK_TIERS = [EscalationRiskTier.CRITICAL, EscalationRiskTier.AT_RISK] as const;

export const activeTaskCondition = (): Partial<Pick<ConditionState, "readiness">> => ({
  readiness: { field: "readiness", value: [...ACTIVE_TASK_READINESS], comparator: Comparator.IN },
});

export const isTeamScopedInbox = (inbox: string): inbox is TeamInbox =>
  isOneOf(inbox, InboxValues) && INBOX_SCOPE[inbox] === "TEAM";

export const getDefaultConditions = ({
  inbox,
  personId,
  currentTeamId,
}: {
  inbox: Inbox;
  personId: string;
  currentTeamId: string;
}): ConditionState => {
  const conditions: ConditionState = { ...emptyConditionState };

  switch (inbox) {
    case Inbox["my-inbox"]:
      conditions.assigneeId = { field: "assigneeId", value: personId, comparator: Comparator.EQUALS };
      conditions.snoozed = { field: "snoozed", value: MySqlValue.FALSE, comparator: Comparator.EQUALS };
      conditions.status = { field: "status", value: [...ACTIVE_TICKET_STATUSES], comparator: Comparator.IN };
      conditions.resolutionStage = {
        field: "resolutionStage",
        value: [...TERMINAL_ESCALATION_STAGES],
        comparator: Comparator.NOT_IN,
      };
      Object.assign(conditions, activeTaskCondition());
      conditions.isResolved = { field: "isResolved", value: MySqlValue.FALSE, comparator: Comparator.EQUALS };
      conditions.dueDate = { field: "dueDate", value: MySqlValue.TRUE, comparator: Comparator.EQUALS };
      break;
    case Inbox["my-created"]:
      conditions.type = { field: "type", value: InboxItemType.TICKET, comparator: Comparator.EQUALS };
      conditions.createdById = { field: "createdById", value: personId, comparator: Comparator.EQUALS };
      break;
    case Inbox["my-tickets"]:
      conditions.type = { field: "type", value: InboxItemType.TICKET, comparator: Comparator.EQUALS };
      conditions.assigneeId = { field: "assigneeId", value: personId, comparator: Comparator.EQUALS };
      conditions.snoozed = { field: "snoozed", value: MySqlValue.FALSE, comparator: Comparator.EQUALS };
      conditions.status = { field: "status", value: [...OPEN_TICKET_STATUSES], comparator: Comparator.IN };
      break;
    case Inbox["my-triage"]:
      conditions.type = {
        field: "type",
        value: [InboxItemType.TICKET, InboxItemType.ESCALATION],
        comparator: Comparator.IN,
      };
      conditions.assigneeId = { field: "assigneeId", value: personId, comparator: Comparator.EQUALS };
      conditions.snoozed = { field: "snoozed", value: MySqlValue.FALSE, comparator: Comparator.EQUALS };
      conditions.status = { field: "status", value: [...TRIAGE_TICKET_STATUSES], comparator: Comparator.IN };
      conditions.resolutionStage = {
        field: "resolutionStage",
        value: [...TRIAGE_ESCALATION_STAGES],
        comparator: Comparator.IN,
      };
      break;
    case Inbox["my-conversations"]:
      conditions.type = { field: "type", value: InboxItemType.CONVERSATION, comparator: Comparator.EQUALS };
      conditions.assigneeId = { field: "assigneeId", value: personId, comparator: Comparator.EQUALS };
      conditions.snoozed = { field: "snoozed", value: MySqlValue.FALSE, comparator: Comparator.EQUALS };
      conditions.isResolved = { field: "isResolved", value: MySqlValue.FALSE, comparator: Comparator.EQUALS };
      break;
    case Inbox["my-tasks"]:
      conditions.type = { field: "type", value: InboxItemType.TASK, comparator: Comparator.EQUALS };
      conditions.assigneeId = { field: "assigneeId", value: personId, comparator: Comparator.EQUALS };
      Object.assign(conditions, activeTaskCondition());
      conditions.dueDate = { field: "dueDate", value: MySqlValue.TRUE, comparator: Comparator.EQUALS };
      break;
    case Inbox["my-escalations"]:
      conditions.type = { field: "type", value: InboxItemType.ESCALATION, comparator: Comparator.EQUALS };
      conditions.assigneeId = { field: "assigneeId", value: personId, comparator: Comparator.EQUALS };
      conditions.snoozed = { field: "snoozed", value: MySqlValue.FALSE, comparator: Comparator.EQUALS };
      conditions.resolutionStage = {
        field: "resolutionStage",
        value: [...ACTIVE_ESCALATION_STAGES],
        comparator: Comparator.IN,
      };
      break;
    case Inbox["my-urgent"]:
      conditions.type = { field: "type", value: InboxItemType.TICKET, comparator: Comparator.EQUALS };
      conditions.assigneeId = { field: "assigneeId", value: personId, comparator: Comparator.EQUALS };
      conditions.status = { field: "status", value: [...ACTIVE_TICKET_STATUSES], comparator: Comparator.IN };
      conditions.priority = { field: "priority", value: TicketPriority.URGENT, comparator: Comparator.EQUALS };
      conditions.snoozed = { field: "snoozed", value: MySqlValue.FALSE, comparator: Comparator.EQUALS };
      break;
    case Inbox["my-snoozed"]:
      conditions.type = {
        field: "type",
        value: [InboxItemType.TICKET, InboxItemType.ESCALATION],
        comparator: Comparator.IN,
      };
      conditions.assigneeId = { field: "assigneeId", value: personId, comparator: Comparator.EQUALS };
      conditions.status = { field: "status", value: [...ACTIVE_TICKET_STATUSES], comparator: Comparator.IN };
      conditions.snoozed = { field: "snoozed", value: MySqlValue.TRUE, comparator: Comparator.EQUALS };
      conditions.resolutionStage = {
        field: "resolutionStage",
        value: [...TERMINAL_ESCALATION_STAGES],
        comparator: Comparator.NOT_IN,
      };
      break;
    case Inbox["open-tickets"]:
      conditions.type = { field: "type", value: InboxItemType.TICKET, comparator: Comparator.EQUALS };
      conditions.status = { field: "status", value: [...OPEN_TICKET_STATUSES], comparator: Comparator.IN };
      conditions.teamId = { field: "teamId", value: currentTeamId, comparator: Comparator.EQUALS };
      conditions.snoozed = { field: "snoozed", value: MySqlValue.FALSE, comparator: Comparator.EQUALS };
      break;
    case Inbox["triage-tickets"]:
      conditions.type = { field: "type", value: InboxItemType.TICKET, comparator: Comparator.EQUALS };
      conditions.status = { field: "status", value: [...TRIAGE_TICKET_STATUSES], comparator: Comparator.IN };
      conditions.teamId = { field: "teamId", value: currentTeamId, comparator: Comparator.EQUALS };
      conditions.snoozed = { field: "snoozed", value: MySqlValue.FALSE, comparator: Comparator.EQUALS };
      break;
    case Inbox["open-conversations"]:
      conditions.type = { field: "type", value: InboxItemType.CONVERSATION, comparator: Comparator.EQUALS };
      conditions.teamId = { field: "teamId", value: currentTeamId, comparator: Comparator.EQUALS };
      conditions.isResolved = { field: "isResolved", value: MySqlValue.FALSE, comparator: Comparator.EQUALS };
      break;
    case Inbox["active-escalations"]:
      conditions.type = { field: "type", value: InboxItemType.ESCALATION, comparator: Comparator.EQUALS };
      conditions.teamId = { field: "teamId", value: currentTeamId, comparator: Comparator.EQUALS };
      conditions.snoozed = { field: "snoozed", value: MySqlValue.FALSE, comparator: Comparator.EQUALS };
      conditions.riskTier = { field: "riskTier", value: [...BANNER_RISK_TIERS], comparator: Comparator.IN };
      conditions.resolutionStage = {
        field: "resolutionStage",
        value: [...ACTIVE_ESCALATION_STAGES],
        comparator: Comparator.IN,
      };
      break;
    case Inbox["triage-escalations"]:
      conditions.type = { field: "type", value: InboxItemType.ESCALATION, comparator: Comparator.EQUALS };
      conditions.teamId = { field: "teamId", value: currentTeamId, comparator: Comparator.EQUALS };
      conditions.snoozed = { field: "snoozed", value: MySqlValue.FALSE, comparator: Comparator.EQUALS };
      conditions.riskTier = { field: "riskTier", value: [...BANNER_RISK_TIERS], comparator: Comparator.IN };
      conditions.resolutionStage = {
        field: "resolutionStage",
        value: [...TRIAGE_ESCALATION_STAGES],
        comparator: Comparator.IN,
      };
      break;
    case Inbox["unassigned-escalations"]:
      conditions.type = { field: "type", value: InboxItemType.ESCALATION, comparator: Comparator.EQUALS };
      conditions.teamId = { field: "teamId", value: currentTeamId, comparator: Comparator.EQUALS };
      conditions.assigneeId = { field: "assigneeId", value: MySqlValue.NULL, comparator: Comparator.EQUALS };
      conditions.snoozed = { field: "snoozed", value: MySqlValue.FALSE, comparator: Comparator.EQUALS };
      conditions.riskTier = { field: "riskTier", value: [...BANNER_RISK_TIERS], comparator: Comparator.IN };
      conditions.resolutionStage = {
        field: "resolutionStage",
        value: [...TERMINAL_ESCALATION_STAGES],
        comparator: Comparator.NOT_IN,
      };
      break;
    case Inbox["open-tasks"]:
      conditions.type = { field: "type", value: InboxItemType.TASK, comparator: Comparator.EQUALS };
      conditions.teamId = { field: "teamId", value: currentTeamId, comparator: Comparator.EQUALS };
      Object.assign(conditions, activeTaskCondition());
      conditions.dueDate = { field: "dueDate", value: MySqlValue.TRUE, comparator: Comparator.EQUALS };
      break;
    case Inbox["unassigned-tasks"]:
      conditions.type = { field: "type", value: InboxItemType.TASK, comparator: Comparator.EQUALS };
      conditions.teamId = { field: "teamId", value: currentTeamId, comparator: Comparator.EQUALS };
      conditions.assigneeId = { field: "assigneeId", value: MySqlValue.NULL, comparator: Comparator.EQUALS };
      Object.assign(conditions, activeTaskCondition());
      break;
    case Inbox["unassigned-tickets"]:
      conditions.type = { field: "type", value: InboxItemType.TICKET, comparator: Comparator.EQUALS };
      conditions.assigneeId = { field: "assigneeId", value: MySqlValue.NULL, comparator: Comparator.EQUALS };
      conditions.teamId = { field: "teamId", value: currentTeamId, comparator: Comparator.EQUALS };
      conditions.snoozed = { field: "snoozed", value: MySqlValue.FALSE, comparator: Comparator.EQUALS };
      conditions.status = { field: "status", value: [...ACTIVE_TICKET_STATUSES], comparator: Comparator.IN };
      break;
    case Inbox["unassigned-conversations"]:
      conditions.type = { field: "type", value: InboxItemType.CONVERSATION, comparator: Comparator.EQUALS };
      conditions.assigneeId = { field: "assigneeId", value: MySqlValue.NULL, comparator: Comparator.EQUALS };
      conditions.teamId = { field: "teamId", value: currentTeamId, comparator: Comparator.EQUALS };
      conditions.isResolved = { field: "isResolved", value: MySqlValue.FALSE, comparator: Comparator.EQUALS };
      break;
    case Inbox["urgent-tickets"]:
      conditions.type = { field: "type", value: InboxItemType.TICKET, comparator: Comparator.EQUALS };
      conditions.status = { field: "status", value: [...ACTIVE_TICKET_STATUSES], comparator: Comparator.IN };
      conditions.priority = { field: "priority", value: TicketPriority.URGENT, comparator: Comparator.EQUALS };
      conditions.teamId = { field: "teamId", value: currentTeamId, comparator: Comparator.EQUALS };
      conditions.snoozed = { field: "snoozed", value: MySqlValue.FALSE, comparator: Comparator.EQUALS };
      break;
    case Inbox["snoozed"]:
      conditions.type = {
        field: "type",
        value: [InboxItemType.TICKET, InboxItemType.ESCALATION],
        comparator: Comparator.IN,
      };
      conditions.status = { field: "status", value: [...ACTIVE_TICKET_STATUSES], comparator: Comparator.IN };
      conditions.snoozed = { field: "snoozed", value: MySqlValue.TRUE, comparator: Comparator.EQUALS };
      conditions.teamId = { field: "teamId", value: currentTeamId, comparator: Comparator.EQUALS };
      conditions.resolutionStage = {
        field: "resolutionStage",
        value: [...TERMINAL_ESCALATION_STAGES],
        comparator: Comparator.NOT_IN,
      };
      break;
    case Inbox["custom"]:
      break;
  }

  return conditions;
};

const collectConditions = (
  conditions: ConditionState,
  superConditions: ConditionState,
  fields: ConditionFieldArray,
  specialHandlers?: Partial<Record<keyof ConditionState, (condition: Condition) => Condition | Definition | undefined>>,
): (Condition | Definition)[] => {
  const cons: (Condition | Definition)[] = [];

  fields.forEach((field) => {
    const superCondition = superConditions[field];
    const condition = conditions[field];
    const handler = specialHandlers?.[field];

    if (superCondition) {
      const result = handler ? handler(superCondition) : superCondition;
      if (result) {
        cons.push(result);
      }
    }
    if (condition) {
      const result = handler ? handler(condition) : condition;
      if (result) {
        cons.push(result);
      }
    }
  });

  return cons;
};

const isTypePermitted = (conditions: ConditionState, superConditions: ConditionState, type: InboxItemType) => {
  if (Array.isArray(superConditions.type?.value) && !superConditions.type.value.includes(type)) {
    return false;
  }
  if (typeof superConditions.type?.value === "string" && superConditions.type.value !== type) {
    return false;
  }
  if (Array.isArray(conditions.type?.value) && !conditions.type.value.includes(type)) {
    return false;
  }
  if (typeof conditions.type?.value === "string" && conditions.type.value !== type) {
    return false;
  }
  return true;
};

const shouldGetDefinition = (conditions: ConditionState, superConditions: ConditionState, type: InboxItemType) => {
  if (!isTypePermitted(conditions, superConditions, type)) {
    return false;
  }
  const projectCategory = conditions.projectCategory ?? superConditions.projectCategory;
  return (
    projectCategory === undefined ||
    type === InboxItemType.TASK ||
    !isTypePermitted(conditions, superConditions, InboxItemType.TASK)
  );
};

export const getTicketDefinition = (
  conditions: ConditionState,
  superConditions: ConditionState,
): Definition | undefined => {
  if (!shouldGetDefinition(conditions, superConditions, InboxItemType.TICKET)) {
    return undefined;
  }

  const cons = collectConditions(conditions, superConditions, ticketFields, {
    snoozed: getSnoozeDefinition,
    homeownerSentiment: getSentimentDefinition,
    createdAt: getRelativeDateDefinition,
    updatedAt: getRelativeDateDefinition,
    snoozeDate: (condition) => getRelativeDateDefinition({ ...condition, field: "followUpDate" }),
  });

  return {
    operator: "AND",
    conditions: cons,
  };
};

export const getTaskDefinition = (
  conditions: ConditionState,
  superConditions: ConditionState,
): Definition | undefined => {
  if (!shouldGetDefinition(conditions, superConditions, InboxItemType.TASK)) {
    return undefined;
  }

  const cons = collectConditions(conditions, superConditions, taskFields, {
    homeownerSentiment: getSentimentDefinition,
    dueDate: getDueDateDefinition,
    needsReview: getNeedsReviewDefinition,
    createdAt: getRelativeDateDefinition,
    updatedAt: getRelativeDateDefinition,
    followUpDate: getRelativeDateDefinition,
  });

  return {
    operator: "AND",
    conditions: cons,
  };
};

// Only the "on" (TRUE) toggle maps to the task-status predicate; a NOT_IN/FALSE condition is a no-op.
const getNeedsReviewDefinition = (condition: Condition): Condition | undefined =>
  condition.value === MySqlValue.TRUE
    ? { field: "taskStatus", value: [TaskStatus.NEEDS_REVIEW], comparator: Comparator.IN }
    : undefined;

export const getEscalationDefinition = (
  conditions: ConditionState,
  superConditions: ConditionState,
): Definition | undefined => {
  if (!shouldGetDefinition(conditions, superConditions, InboxItemType.ESCALATION)) {
    return undefined;
  }

  const cons = collectConditions(conditions, superConditions, escalationFields, {
    snoozed: getEscalationSnoozeDefinition,
    createdAt: getRelativeDateDefinition,
    updatedAt: getRelativeDateDefinition,
    followUpDate: getRelativeDateDefinition,
  });

  return {
    operator: "AND",
    conditions: cons,
  };
};

const getRelativeDateDefinition = (condition: Condition): Condition | Definition | undefined => {
  if (!Array.isArray(condition.value)) {
    return condition;
  }
  const [gteToken, ltToken] = condition.value;
  if (typeof gteToken !== "string") {
    return undefined;
  }
  const conditions: Condition[] = [
    { field: condition.field, value: gteToken, comparator: Comparator.GREATER_THAN_OR_EQUAL },
  ];
  if (typeof ltToken === "string") {
    conditions.push({ field: condition.field, value: ltToken, comparator: Comparator.LESS_THAN });
  }
  return { operator: "AND", conditions };
};

// Tickets store the snooze on `followUpDate`, escalations on `snoozedUntil` — hence the field param.
const buildSnoozeDefinition =
  (field: "followUpDate" | "snoozedUntil") =>
  (condition: Condition): Definition => {
    if (condition.value === MySqlValue.TRUE) {
      return {
        operator: "AND",
        conditions: [
          { field, value: MySqlValue.NULL, comparator: Comparator.NOT_EQUALS },
          { field, value: MySqlValue.NOW, comparator: Comparator.GREATER_THAN },
        ],
      };
    }
    return {
      operator: "OR",
      conditions: [
        { field, value: MySqlValue.NULL, comparator: Comparator.EQUALS },
        { field, value: MySqlValue.NOW, comparator: Comparator.LESS_THAN },
      ],
    };
  };

const getSnoozeDefinition = buildSnoozeDefinition("followUpDate");
const getEscalationSnoozeDefinition = buildSnoozeDefinition("snoozedUntil");

const getDueDateDefinition = (condition: Condition): Definition => {
  // Both options selected arrives as { value: [TRUE, FALSE], comparator: IN } — that's every
  // due-date state, so impose no real constraint (a tautology matching nulls and non-nulls alike).
  if (Array.isArray(condition.value)) {
    return {
      operator: "OR",
      conditions: [
        {
          field: "followUpDate",
          value: MySqlValue.NULL,
          comparator: Comparator.EQUALS,
        },
        {
          field: "followUpDate",
          value: MySqlValue.NULL,
          comparator: Comparator.NOT_EQUALS,
        },
      ],
    };
  }
  if (condition.value === MySqlValue.TRUE) {
    return {
      operator: "OR",
      conditions: [
        {
          field: "followUpDate",
          value: MySqlValue.NULL,
          comparator: Comparator.EQUALS,
        },
        {
          field: "followUpDate",
          value: TASK_DUE_DATE_BOUNDARY_TOKEN,
          comparator: Comparator.LESS_THAN,
        },
      ],
    };
  }
  return {
    operator: "OR",
    conditions: [
      {
        field: "followUpDate",
        value: MySqlValue.NULL,
        comparator: Comparator.EQUALS,
      },
      {
        field: "followUpDate",
        value: TASK_DUE_DATE_BOUNDARY_TOKEN,
        comparator: Comparator.GREATER_THAN_OR_EQUAL,
      },
    ],
  };
};

const getSentimentDefinition = (condition: Condition): Definition | Condition => {
  if (typeof condition.value === "number") {
    return condition;
  }

  if (condition.comparator === Comparator.EQUALS && typeof condition.value === "string") {
    const range = getSentimentRange(condition.value);

    return {
      operator: "AND",
      conditions: [
        {
          field: condition.field,
          value: range.min,
          comparator: Comparator.GREATER_THAN_OR_EQUAL,
        },
        {
          field: condition.field,
          value: range.max,
          comparator: Comparator.LESS_THAN_OR_EQUAL,
        },
      ],
    };
  }

  if (condition.comparator === Comparator.IN && Array.isArray(condition.value)) {
    const rangeConditions: (Condition | Definition)[] = [];

    condition.value.forEach((val) => {
      if (typeof val === "string") {
        const range = getSentimentRange(val);
        rangeConditions.push({
          operator: "AND",
          conditions: [
            {
              field: condition.field,
              value: range.min,
              comparator: Comparator.GREATER_THAN_OR_EQUAL,
            },
            {
              field: condition.field,
              value: range.max,
              comparator: Comparator.LESS_THAN_OR_EQUAL,
            },
          ],
        });
      }
    });

    if (rangeConditions.length === 0) {
      return condition;
    }

    if (rangeConditions[0]) {
      return rangeConditions[0];
    }

    return {
      operator: "OR",
      conditions: rangeConditions,
    };
  }

  return condition;
};

const SENTIMENT_VALUE_TO_RANGE = {
  "0-2": { min: 0, max: 24 },
  "3-4": { min: 25, max: 44 },
  "5-6": { min: 45, max: 64 },
  "7-8": { min: 65, max: 84 },
  "9-10": { min: 85, max: 100 },
} as const;

const SENTIMENT_VALUES = [
  "0-2",
  "3-4",
  "5-6",
  "7-8",
  "9-10",
] as const satisfies (keyof typeof SENTIMENT_VALUE_TO_RANGE)[];

const getSentimentRange = (val: string) => {
  const value = SENTIMENT_VALUES.find((v) => v === val);
  if (!value) {
    throw new RemiErrorDeprecated({ message: `Invalid sentiment value: ${val}` });
  }
  return SENTIMENT_VALUE_TO_RANGE[value];
};

export const getConversationDefinition = (
  conditions: ConditionState,
  superConditions: ConditionState,
): Definition | undefined => {
  if (!shouldGetDefinition(conditions, superConditions, InboxItemType.CONVERSATION)) {
    return undefined;
  }

  const cons = collectConditions(conditions, superConditions, conversationFields, {
    homeownerSentiment: getSentimentDefinition,
    createdAt: getRelativeDateDefinition,
    updatedAt: getRelativeDateDefinition,
    lastUnresolvedAt: getRelativeDateDefinition,
  });

  return {
    operator: "AND",
    conditions: cons,
  };
};

export const getSortDefinition = (sort: SortDefinition["sort"][number]): SortDefinition => {
  return {
    sort: [sort],
  };
};

export const getInboxSearchInput = (
  conditions: ConditionState,
  superConditions: ConditionState,
  sort: SortDefinition["sort"][number],
) => {
  return {
    search: "" as const,
    ticketDefinition: getTicketDefinition(conditions, superConditions),
    conversationDefinition: getConversationDefinition(conditions, superConditions),
    taskDefinition: getTaskDefinition(conditions, superConditions),
    escalationDefinition: getEscalationDefinition(conditions, superConditions),
    sort: getSortDefinition(sort),
  };
};
